import React, { useRef } from 'react'
import { WealthboxWorkflow } from '../../../../../../../models/Integration'
import { User } from '../../../../../../../models/User'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { Controller, useForm } from 'react-hook-form'
import { useUpdateIntegrationEntityMutation } from '../../../../../../../store/integrations/integrations'
import { useOutsideClick } from '../../../../../../../common/useOutsideClick'
import { Form, Overlay } from 'react-bootstrap'
import { SelectDropdown } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../../../components/Button/Button'

interface CompleteWealthboxWorkflowStepFormValues {
  workflowOutcomeId?: string | null
}

interface CompleteWealthboxWorkflowStepOverlayProps {
  targetRef: React.RefObject<HTMLElement | null>
  containerRef: React.RefObject<HTMLElement | null>
  show: boolean
  onHide: () => void
  onWorkflowStepCompleted?: (workflow: WealthboxWorkflow) => void
  workflow: WealthboxWorkflow
  contact: User
  serviceId: string
}

export const CompleteWealthboxWorkflowStepOverlay: React.FC<CompleteWealthboxWorkflowStepOverlayProps> = ({
  targetRef,
  containerRef,
  show,
  onHide,
  workflow,
  onWorkflowStepCompleted,
  contact,
  serviceId,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()

  const defaultValues = { workflowOutcomeId: null }
  const { control, handleSubmit, reset } = useForm<CompleteWealthboxWorkflowStepFormValues>({ defaultValues })
  const overlayRef = useRef(null)

  const [triggerUpdateIntegrationEntity, { isLoading: isCreatingWorkflow }] = useUpdateIntegrationEntityMutation()

  const outcomeOptions =
    workflow.activeStep?.workflowOutcomes.map((outcome) => {
      return { id: `${outcome.id}`, label: outcome.name ?? `Outcome id: ${outcome.id}` }
    }) ?? []

  useOutsideClick(overlayRef, () => {
    onHide()
  })

  const _handleWorkflowStepCompleted = async (formValues: CompleteWealthboxWorkflowStepFormValues) => {
    try {
      const { data: updatedWorkflow } = await triggerUpdateIntegrationEntity({
        accountId,
        relatedTo: { id: '' },
        connector: serviceId,
        entityType: 'workflow_steps',
        entityId: `${workflow.id}-${workflow.activeStep?.id}`,
        payload: { complete: true, workflow_outcome_id: formValues.workflowOutcomeId },
      }).unwrap()
      reset()
      onWorkflowStepCompleted &&
        onWorkflowStepCompleted({
          id: updatedWorkflow.id,
          activeStep: updatedWorkflow.activeStep,
          name: updatedWorkflow.name,
          description: updatedWorkflow.description,
          label: updatedWorkflow.label,
        } as WealthboxWorkflow)
    } catch (e) {}
  }

  const handleCancel = async (e: React.MouseEvent<HTMLButtonElement>) => {
    reset()
    onHide()
  }

  const handleWorkflowStepCompleted = tracked(
    _handleWorkflowStepCompleted,
    {
      eventName: TrackEvent.UPDATE_WORKFLOW_STEP_IN_CRM,
    },
    {
      eventDetailsExtractor: (formValues: CompleteWealthboxWorkflowStepFormValues) => {
        return {
          connector: serviceId,
          contactId: contact.id,
          workflowOutcomeId: formValues.workflowOutcomeId,
        }
      },
    },
  )

  return (
    <Overlay ref={overlayRef} target={targetRef.current} container={containerRef?.current} show={show} placement='right'>
      {({ placement: _placement, arrowProps: _arrowProps, show: _show, popper: _popper, hasDoneInitialMeasure: _hasDoneInitialMeasure, ...props }) => (
        <div className='start-workflow-overlay d-flex flex-column gap-3 border-default zocks-border-0.25 p-4' {...props}>
          <h4>Complete {workflow.activeStep?.name}</h4>
          <Controller
            name='workflowOutcomeId'
            control={control}
            rules={{ required: 'Outcome is required' }}
            render={({ field, fieldState }) => (
              <div>
                <Form.Label>Select outcome</Form.Label>
                <SelectDropdown
                  initialSelectedOption={null}
                  options={outcomeOptions}
                  className='py-2 zocks-px-0.75'
                  onOptionSelected={(option) => field.onChange(option.id)}
                  placeholder={`Select outcome`}
                />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </div>
            )}
          />
          <div className='align-self-end d-flex gap-2'>
            <Button variant='secondary' className='button-small' onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant='primary' className='button-small' onClick={handleSubmit(handleWorkflowStepCompleted)} loading={isCreatingWorkflow}>
              <i className='bi bi-check' />
              Complete
            </Button>
          </div>
        </div>
      )}
    </Overlay>
  )
}
